import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { doc, updateDoc } from 'firebase/firestore'
import { changePassword, getCurrentUser } from '../../../services/authService'
import { db } from '../../../config/firebase'

const ChangePassword = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,      // >= 8 characters
    hasUpperCase: false,      // Contains A-Z
    hasLowerCase: false,      // Contains a-z
    hasNumber: false,         // Contains 0-9
    hasSpecialChar: false     // Contains !@#$%^&*
  })

  /**
   * Validate password strength in real-time
   * Updates passwordStrength state with boolean values for each requirement
   */
  const validatePasswordStrength = (password) => {
    setPasswordStrength({
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    })
  }

  /**
   * Check if password meets all strength requirements
   */
  const isPasswordStrong = () => {
    return Object.values(passwordStrength).every(requirement => requirement === true)
  }

  /**
   * Handle input changes
   * Triggers real-time validation for new password field
   */
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value
    })

    // Real-time validation for new password
    if (name === 'newPassword') {
      validatePasswordStrength(value)
    }

    setError('') // Clear errors on input change
  }

  /**
   * Handle form submission
   * Validates password strength and calls changePassword service
   */
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccessMessage('')

    // Validation checks
    if (!formData.currentPassword) {
      setError('Current password is required')
      return
    }

    if (!isPasswordStrong()) {
      setError('New password does not meet strength requirements')
      return
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match')
      return
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('New password must be different from current password')
      return
    }

    setLoading(true)

    const result = await changePassword(formData.currentPassword, formData.newPassword)

    setLoading(false)

    if (result.success) {
      // Update Firestore to clear password change requirement flag
      try {
        const currentUser = getCurrentUser()
        if (currentUser) {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            passwordChangeRequired: false,
            updatedAt: new Date().toISOString()
          })
        }
      } catch (firestoreError) {
        console.error('Failed to update password change flag:', firestoreError)
        // Don't show error to user - password was already changed successfully in Firebase Auth
      }

      setSuccessMessage('Password changed successfully!')
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard')
      }, 2000)
    } else {
      setError(result.error)
    }
  }

  return (
    <div className="psyber-container psyber-portal-container">
      {/* Background Effects */}
      <div className="psyber-bg-effects">
        <div className="psyber-orb psyber-orb-1"></div>
        <div className="psyber-orb psyber-orb-2"></div>
        <div className="psyber-grid"></div>
      </div>

      {/* Change Password Card */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh'
      }}>
        <div className="psyber-card" style={{
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          {/* Title */}
          <div style={{ marginBottom: '2rem' }}>
            <h1 className="psyber-title-gradient" style={{ marginBottom: '0.5rem' }}>
              Change Password
            </h1>
            <p style={{ color: 'var(--psyber-text-secondary)' }}>
              Update your account password
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
            {/* Error Message */}
            {error && (
              <div style={{
                padding: '0.75rem',
                marginBottom: '1rem',
                background: 'var(--psyber-error-bg)',
                color: 'var(--psyber-error)',
                borderRadius: 'var(--psyber-radius-md)',
                border: '1px solid var(--psyber-error)',
                fontSize: 'var(--psyber-font-size-sm)'
              }}>
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div style={{
                padding: '0.75rem',
                marginBottom: '1rem',
                background: 'var(--psyber-success-bg)',
                color: 'var(--psyber-success)',
                borderRadius: 'var(--psyber-radius-md)',
                border: '1px solid var(--psyber-success)',
                fontSize: 'var(--psyber-font-size-sm)'
              }}>
                <i className="fas fa-check-circle"></i> {successMessage}
              </div>
            )}

            {/* Current Password Input */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="currentPassword" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                <i className="fas fa-lock"></i> Current Password
              </label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                className="psyber-input"
                placeholder="Enter your current password"
                value={formData.currentPassword}
                onChange={handleChange}
                required
                autoComplete="current-password"
              />
            </div>

            {/* New Password Input */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="newPassword" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                <i className="fas fa-key"></i> New Password
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                className="psyber-input"
                placeholder="Enter your new password"
                value={formData.newPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />

              {/* Password Strength Indicator - Only show when new password field has value */}
              {formData.newPassword && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.75rem',
                  background: 'var(--psyber-card-bg)',
                  border: '1px solid var(--psyber-card-border)',
                  borderRadius: 'var(--psyber-radius-md)',
                  fontSize: 'var(--psyber-font-size-sm)'
                }}>
                  <p style={{
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    color: 'var(--psyber-text)'
                  }}>
                    Password Requirements:
                  </p>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    <li style={{
                      color: passwordStrength.hasMinLength ? 'var(--psyber-success)' : 'var(--psyber-text-secondary)',
                      marginBottom: '0.25rem'
                    }}>
                      <i className={`fas fa-${passwordStrength.hasMinLength ? 'check-circle' : 'circle'}`}></i>
                      {' '}At least 8 characters
                    </li>
                    <li style={{
                      color: passwordStrength.hasUpperCase ? 'var(--psyber-success)' : 'var(--psyber-text-secondary)',
                      marginBottom: '0.25rem'
                    }}>
                      <i className={`fas fa-${passwordStrength.hasUpperCase ? 'check-circle' : 'circle'}`}></i>
                      {' '}Contains uppercase letter (A-Z)
                    </li>
                    <li style={{
                      color: passwordStrength.hasLowerCase ? 'var(--psyber-success)' : 'var(--psyber-text-secondary)',
                      marginBottom: '0.25rem'
                    }}>
                      <i className={`fas fa-${passwordStrength.hasLowerCase ? 'check-circle' : 'circle'}`}></i>
                      {' '}Contains lowercase letter (a-z)
                    </li>
                    <li style={{
                      color: passwordStrength.hasNumber ? 'var(--psyber-success)' : 'var(--psyber-text-secondary)',
                      marginBottom: '0.25rem'
                    }}>
                      <i className={`fas fa-${passwordStrength.hasNumber ? 'check-circle' : 'circle'}`}></i>
                      {' '}Contains number (0-9)
                    </li>
                    <li style={{
                      color: passwordStrength.hasSpecialChar ? 'var(--psyber-success)' : 'var(--psyber-text-secondary)'
                    }}>
                      <i className={`fas fa-${passwordStrength.hasSpecialChar ? 'check-circle' : 'circle'}`}></i>
                      {' '}Contains special character (!@#$%^&*)
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Confirm New Password Input */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="confirmPassword" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                <i className="fas fa-shield-alt"></i> Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className="psyber-input"
                placeholder="Re-enter your new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="psyber-btn psyber-btn-primary"
              disabled={loading}
              style={{ width: '100%' }}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Changing Password...
                </>
              ) : (
                <>
                  <i className="fas fa-check-circle"></i>
                  Change Password
                </>
              )}
            </button>
          </form>

          {/* Footer Link */}
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid var(--psyber-card-border)',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <p>
              <Link to="/dashboard" style={{
                color: 'var(--psyber-primary)',
                fontWeight: 600
              }}>
                <i className="fas fa-arrow-left"></i> Back to Dashboard
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChangePassword
